import React from 'react';
import CodeBlock from './CodeBlock';
import Callout from './Callout';
import MermaidDiagram from './MermaidDiagram';

// Custom link component that handles internal routing and external links properly
const CustomLink = (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const href = props.href || '';
  const isInternalLink = href && (href.startsWith('/') || href.startsWith('#'));

  if (isInternalLink) {
    // In a real app, you might use React Router's <Link> here if it's an absolute internal path
    // For now, we'll just style it nicely
    return (
      <a href={href} className="font-medium text-primary underline underline-offset-4 decoration-primary/30 hover:decoration-primary transition-all" {...props}>
        {props.children}
      </a>
    );
  }

  return (
    <a 
      target="_blank" 
      rel="noopener noreferrer" 
      className="font-medium text-primary underline underline-offset-4 decoration-primary/30 hover:decoration-primary transition-all inline-flex items-center gap-1"
      {...props}
    >
      {props.children}
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-70"><path d="M7 7h10v10"/><path d="M7 17 17 7"/></svg>
    </a>
  );
};

export const components = {
  h2: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight mt-10 mb-4 first:mt-0" {...props} />
  ),
  h3: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3 className="scroll-m-20 text-2xl font-semibold tracking-tight mt-8 mb-4" {...props} />
  ),
  a: CustomLink,
  p: ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className="leading-7 [&:not(:first-child)]:mt-6 text-muted-foreground/90 text-lg" {...props} />
  ),
  ul: ({ className, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className="my-6 ml-6 list-disc [&>li]:mt-2 text-muted-foreground/90 text-lg" {...props} />
  ),
  ol: ({ className, ...props }: React.HTMLAttributes<HTMLOListElement>) => (
    <ol className="my-6 ml-6 list-decimal [&>li]:mt-2 text-muted-foreground/90 text-lg" {...props} />
  ),
  li: ({ className, ...props }: React.HTMLAttributes<HTMLLIElement>) => (
    <li className="leading-7" {...props} />
  ),
  blockquote: ({ className, ...props }: React.HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote className="mt-6 border-l-2 border-primary pl-6 italic text-muted-foreground" {...props} />
  ),
  // Handle inline code specifically, avoiding wrapping code blocks
  code: ({ node, inline, className, children, ...props }: any) => {
    // If it's not inline and has a class name (meaning it's from a code block),
    // we let the pre tag handler take care of it, or handle it here.
    if (!className) {
      return (
        <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold text-primary/80" {...props}>
          {children}
        </code>
      );
    }
    return <code className={className} {...props}>{children}</code>;
  },
  pre: (props: any) => {
    // Extract the children of the pre tag which is typically a code tag in standard MDX
    const codeElement = React.Children.toArray(props.children)[0] as React.ReactElement;
    if (codeElement && codeElement.type === 'code') {
      const isMermaid = codeElement.props.className === 'language-mermaid';
      if (isMermaid) {
        return <MermaidDiagram chart={String(codeElement.props.children)} />;
      }
      return <CodeBlock className={codeElement.props.className}>{codeElement.props.children}</CodeBlock>;
    }
    return <pre {...props} />;
  },
  img: ({ className, alt, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => (
    <figure className="my-8">
      <img className="rounded-xl border border-border/50 bg-muted/20" alt={alt} {...props} />
      {alt && <figcaption className="mt-3 text-center text-sm text-muted-foreground">{alt}</figcaption>}
    </figure>
  ),
  hr: ({ ...props }) => <hr className="my-8 border-border/50" {...props} />,
  // Export custom components so they can be used directly in MDX without importing
  Callout,
};
